import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getCapturedImageDisplayUrl } from '../services/s3/captureUrlService';
import { Gender } from '@gamma/shared/attributes';
import { GenderLabels } from '@gamma/shared/labels';

/**
 * PersonList - Compact, work-like table view of persons with infinite scroll and multi-criteria search
 */
export default function PersonList({ persons, onPersonClick, onDelete }) {
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [displayedCount, setDisplayedCount] = useState(100);
  const loaderRef = useRef(null);
  const tableContainerRef = useRef(null);

  // Handle search button click
  const handleSearch = () => {
    setActiveSearch(searchInput);
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Filter persons based on active search (multi-criteria)
  const filteredPersons = useMemo(() => {
    if (!activeSearch.trim()) {
      return persons;
    }

    // Split by comma and trim each criterion
    const criteria = activeSearch.split(',').map(c => c.trim().toLowerCase()).filter(c => c);

    if (criteria.length === 0) {
      return persons;
    }

    // Person must match ALL criteria (AND logic)
    return persons.filter(person => {
      return criteria.every(criterion => {
        return (
          person.person_id?.toLowerCase().includes(criterion) ||
          person.first_name?.toLowerCase().includes(criterion) ||
          person.last_name?.toLowerCase().includes(criterion) ||
          `${person.first_name} ${person.last_name}`.toLowerCase().includes(criterion) ||
          person.headline?.toLowerCase().includes(criterion) ||
          person.persontype?.toLowerCase().includes(criterion) ||
          person.group?.toLowerCase().includes(criterion) ||
          person.dataset?.toLowerCase().includes(criterion) ||
          person.searchstring?.toLowerCase().includes(criterion)
        );
      });
    });
  }, [persons, activeSearch]);

  // Reset displayed count when persons array or search changes
  useEffect(() => {
    console.log('[PersonList] Filter applied - Total:', persons.length, 'Filtered:', filteredPersons.length, 'Search:', activeSearch);
    setDisplayedCount(100);
  }, [persons, activeSearch]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && displayedCount < filteredPersons.length) {
          const newCount = Math.min(displayedCount + 50, filteredPersons.length);
          console.log('[PersonList] Loading more:', displayedCount, '→', newCount);
          setDisplayedCount(newCount);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [displayedCount, filteredPersons.length]);

  const displayedPersons = filteredPersons.slice(0, displayedCount);

  function getImageUrl(person) {
    if (!person.image) return null;
    // personManagementAPI returns full URLs (converted from S3 keys)
    return person.image;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
      {/* Search Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search: labour, mp  (comma-separated, matches all)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setActiveSearch('');
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper'
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSearch}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              borderRadius: 1
            }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </Paper>

      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          flex: 1,
          overflow: 'auto'
        }}
      >
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, fontSize: '0.8125rem' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 50 }}>
                Img
              </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 80 }}>
              ID
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary' }}>
              Name
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 100 }}>
              Type
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 80 }}>
              Group
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 70 }}>
              Gender
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 70 }}>
              Age
            </TableCell>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 100 }}>
              Datasets
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase', color: 'text.secondary', width: 80 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedPersons.map((person) => (
            <TableRow
              key={person.person_id}
              hover
              onClick={() => onPersonClick(person)}
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                '&:last-child td': { borderBottom: 0 }
              }}
            >
              {/* Image */}
              <TableCell sx={{ p: 0.5 }}>
                {getImageUrl(person) ? (
                  <Box
                    component="img"
                    src={getImageUrl(person)}
                    alt={`${person.first_name} ${person.last_name}`}
                    sx={{
                      width: 40,
                      height: 40,
                      objectFit: 'cover',
                      borderRadius: 0.5,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: 0.5,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>—</Typography>
                  </Box>
                )}
              </TableCell>

              {/* Person ID */}
              <TableCell>
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    color: 'primary.main'
                  }}
                >
                  {person.person_id}
                </Typography>
              </TableCell>

              {/* Name */}
              <TableCell>
                <Typography sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                  {person.first_name} {person.last_name}
                </Typography>
                {person.headline && (
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {person.headline}
                  </Typography>
                )}
              </TableCell>

              {/* Person Type */}
              <TableCell>
                {person.persontype ? (
                  <Chip
                    label={person.persontype}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 500 }}
                  />
                ) : (
                  <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>—</Typography>
                )}
              </TableCell>

              {/* Group */}
              <TableCell>
                {person.group ? (
                  <Typography sx={{ fontSize: '0.8125rem' }}>{person.group}</Typography>
                ) : (
                  <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>—</Typography>
                )}
              </TableCell>

              {/* Gender */}
              <TableCell>
                {person.physical_attributes?.gender ? (
                  <Chip
                    label={GenderLabels[person.physical_attributes.gender]?.charAt(0) || person.physical_attributes.gender.charAt(0)}
                    size="small"
                    color={person.physical_attributes.gender === Gender.MALE ? 'primary' : person.physical_attributes.gender === Gender.FEMALE ? 'secondary' : 'default'}
                    sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600, width: 28 }}
                  />
                ) : (
                  <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>—</Typography>
                )}
              </TableCell>

              {/* Age Group */}
              <TableCell>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    color: person.physical_attributes?.age_group ? 'text.primary' : 'text.disabled'
                  }}
                >
                  {person.physical_attributes?.age_group || '—'}
                </Typography>
              </TableCell>

              {/* Dataset */}
              <TableCell>
                {person.dataset ? (
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {person.dataset}
                  </Typography>
                ) : (
                  <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>—</Typography>
                )}
              </TableCell>

              {/* Actions */}
              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                  <Tooltip title="Edit" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPersonClick(person);
                      }}
                      sx={{
                        '&:hover': { bgcolor: 'primary.50', color: 'primary.main' }
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(person);
                      }}
                      sx={{
                        '&:hover': { bgcolor: 'error.50', color: 'error.main' }
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Infinite scroll loader */}
      {displayedCount < filteredPersons.length && (
        <Box
          ref={loaderRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2
          }}
        >
          <CircularProgress size={24} />
          <Typography sx={{ ml: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
            Loading more... ({displayedCount} of {filteredPersons.length})
          </Typography>
        </Box>
      )}

      {/* Empty state when no results */}
      {filteredPersons.length === 0 && activeSearch && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            px: 2
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No persons found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No persons match: {activeSearch.split(',').map(c => c.trim()).filter(c => c).join(', ')}
          </Typography>
        </Box>
      )}
      </TableContainer>

      {/* Status bar */}
      <Paper
        elevation={0}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          px: 2,
          py: 1
        }}
      >
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          Showing {displayedCount} of {filteredPersons.length}
          {activeSearch && ` (${persons.length} total)`}
          {displayedCount < filteredPersons.length && ' - scroll down to load more'}
        </Typography>
      </Paper>
    </Box>
  );
}
